import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { attachClosestEdge, extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';

import { colors } from '@constants/colors';

import DashboardCardContainer from './dashboard-card-container';

import './dashboard.scss';

// Individual draggable card
const DraggableCard = ({ card, index, columnSize }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const [closestEdge, setClosestEdge] = useState(null);
  const cardRef = useRef(null);

  // Set up draggable
  useEffect(() => {
    const element = cardRef.current;
    if (!element) {
      return () => {};
    }

    return draggable({
      element,
      getInitialData: () => ({ index, card, columnSize, type: 'dashboard-card' }),
      onGenerateDragPreview: ({ nativeSetDragImage }) => {
        setCustomNativeDragPreview({
          nativeSetDragImage,
          getOffset: pointerOutsideOfPreview({ x: '16px', y: '8px' }),  // eslint-disable-line id-length
          render: ({ container }) => {
            const preview = document.createElement('div');
            preview.style.cssText = `
              padding: 1rem;
              background-color: white;
              border: 1px solid #ccc;
              border-radius: 4px;
              box-shadow: 0 2px 8px rgba(0,0,0,0.15);
              font-size: 0.875rem;
              font-family: Roboto, sans-serif;
              max-width: 200px;
            `;
            preview.textContent = card.title || card.id;
            container.appendChild(preview);
            return () => container.removeChild(preview);
          }
        });
      },
      onDragStart: () => setIsDragging(true),
      onDrop: () => setIsDragging(false)
    });
  }, [index, card, columnSize]);

  // Set up drop target on each card
  useEffect(() => {
    const element = cardRef.current;
    if (!element) {
      return () => {};
    }

    return dropTargetForElements({
      element,
      canDrop: ({ source }) => source.data.type === 'dashboard-card' && source.data.columnSize === columnSize,
      getData: ({ input, element: el }) => {
        return attachClosestEdge({ index, columnSize }, {
          input,
          element: el,
          allowedEdges: ['top', 'bottom']
        });
      },
      onDragEnter: ({ self }) => {
        setIsDraggedOver(true);
        setClosestEdge(extractClosestEdge(self.data));
      },
      onDrag: ({ self }) => {
        setClosestEdge(extractClosestEdge(self.data));
      },
      onDragLeave: () => {
        setIsDraggedOver(false);
        setClosestEdge(null);
      },
      onDrop: () => {
        setIsDraggedOver(false);
        setClosestEdge(null);
      }
    });
  }, [index, columnSize]);

  return (
    <div
      ref={cardRef}
      style={{
        background: !isDragging ? colors.neutral.white : colors.neutral.fog,
        boxShadow: !isDragging ? 'none' : 'rgba(0, 0, 0, 0.12) 0 1px 6px, rgba(0, 0, 0, 0.12) 0 1px 4px',
        userSelect: 'none',
        opacity: isDragging ? 0.4 : 1,
        cursor: 'grab',
        position: 'relative'
      }}
    >
      {/* Drop indicator line */}
      {isDraggedOver && closestEdge === 'top' && (
        <div style={{
          position: 'absolute',
          top: -2,
          left: 0,
          right: 0,
          height: '4px',
          backgroundColor: '#2196F3',
          zIndex: 1,
          borderRadius: '2px'
        }} />
      )}
      {isDraggedOver && closestEdge === 'bottom' && (
        <div style={{
          position: 'absolute',
          bottom: -2,
          left: 0,
          right: 0,
          height: '4px',
          backgroundColor: '#2196F3',
          zIndex: 1,
          borderRadius: '2px'
        }} />
      )}
      <DashboardCardContainer card={card} />
    </div>
  );
};

DraggableCard.propTypes = {
  card: PropTypes.object,
  columnSize: PropTypes.string,
  index: PropTypes.number
};

const DashboardColumn = ({ cards, size, isDragging }) => (
  <div styleName={`col col-${size}`}>
    <div
      style={{
        backgroundColor: isDragging ? '#E3F2FD' : colors.neutral.white,
        minHeight: '100px',
        transition: 'background-color 0.2s ease'
      }}
    >
      {cards.map((card, idx) => (
        <DraggableCard
          card={card}
          columnSize={size === 'large' ? 'LG' : 'SM'}
          index={idx}
          key={card.id}
        />
      ))}
    </div>
  </div>
);

DashboardColumn.propTypes = {
  cards: PropTypes.array,
  isDragging: PropTypes.bool,
  size: PropTypes.string
};

export default DashboardColumn;
